import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  UploadFileTableHeaderColumn,
  UploadFileTableHeaderTrimColumns,
} from "../enums/FileUploadTableHeaderData";
import { useFormContext, useWatch } from "react-hook-form";
import useGetCategoreis from "../hooks/useGetCategoreis";
import {
  getFileSignature,
  formatDisplayTitle,
  getFileTypeDisplay,
  getCategoryTitleById,
} from "../utils/FileUploadFunctions";
import { trimText, useIsMobile } from "../../../../utils/Functions";
import useUser from "../../../../hooks/useUser";
import {
  CLINIC_CATEGORIES_DROPDOWN,
  CLINIC_CATEGORIES_MASTER,
} from "../../../../enums/ApiPaths";

export default function UploadFilesStep({ selectedFiles }) {
  const { roles, currentClinic } = useUser();
  const { ownerId } = useParams();
  const { control } = useFormContext();
  const isMobile = useIsMobile();
  const MAX_TRIM_LENGTH = isMobile ? 10 : 18;
  const watchedFiles = useWatch({ name: "files", control });

  const url = roles?.master
    ? CLINIC_CATEGORIES_MASTER
    : CLINIC_CATEGORIES_DROPDOWN;
  const id = roles?.master ? Number(ownerId) : Number(currentClinic.id);
  const { translatedCategories } = useGetCategoreis(url, id, id);

  const tableRows = useMemo(() => {
    const formFiles = watchedFiles || [];
    return selectedFiles.map((file, index) => {
      const meta = formFiles[index] || {};
      return {
        id: getFileSignature(file),
        Original_title: file.name,
        Title: formatDisplayTitle(meta.title, file.name),
        File_type: getFileTypeDisplay(file.type, file.name),
        File_category: getCategoryTitleById(
          meta.category,
          translatedCategories,
        ),
        __file: file,
      };
    });
  }, [selectedFiles, watchedFiles, translatedCategories]);

  return (
    <div>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: "divider",
          overflow: "hidden",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <TableContainer sx={{ overflowX: "auto", minWidth: 0 }}>
          <Table sx={{ minWidth: 300 }} aria-label="selected files table">
            <UploadFilesTableHeader />

            <TableBody>
              {tableRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {UploadFileTableHeaderColumn.map((column) => {
                    const cellValue = row[column.id] ?? "";
                    const isTrimmedColumn =
                      UploadFileTableHeaderTrimColumns.includes(column.id);
                    const displayValue = isTrimmedColumn
                      ? trimText(String(cellValue), MAX_TRIM_LENGTH)
                      : cellValue;

                    const isValueTrimmed =
                      isTrimmedColumn &&
                      String(cellValue).length > MAX_TRIM_LENGTH;
                    const tableCell = (
                      <TableCell
                        key={column.id}
                        align={column.align ?? "left"}
                        sx={{ textWrap: "nowrap" }}
                      >
                        {displayValue}
                      </TableCell>
                    );

                    // Only show tooltip for trimmed values
                    return isValueTrimmed ? (
                      <Tooltip
                        key={column.id}
                        title={String(cellValue)}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, -30],
                                },
                              },
                            ],
                          },
                        }}
                      >
                        {tableCell}
                      </Tooltip>
                    ) : (
                      tableCell
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

function UploadFilesTableHeader() {
  const { t } = useTranslation();
  return (
    <TableHead>
      <TableRow
        sx={{
          "& th": {
            bgcolor: (theme) =>
              theme.palette.mode === "light"
                ? "grey.50"
                : "rgba(255,255,255,0.04)",
            fontWeight: 700,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.secondary",
            borderBottom: "2px solid",
            borderBottomColor: "divider",
            whiteSpace: "nowrap",
          },
        }}
      >
        {UploadFileTableHeaderColumn.map((col) => (
          <TableCell key={col.id} align={col.align ?? "left"}>
            {t(col.label)}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}
